/**
 * Created by dri on 5/24/18.
 */
import * as _ from 'lodash';
import * as moment from 'moment-timezone';

import { DashboardChannelCollection } from '../../../../collections/DashboardChannels';
import { DashboardWidget } from '../../../../models/DashboardWidget';
import { User } from '../../../../models/User';

const Backbone = require("backbone");

// Configuration interfaces
export interface ChartConfigSeries {
    name: string;
    channelId: number;
    tag: number;
    agg: string;
    valueDecimals?: number;
    valueSuffix?: string;
}

export interface ChartConfigSeriesGroup {
    period: string;
    type: string;
    series: ChartConfigSeries[]
}

export interface ChartConfig {
    seriesGroups: ChartConfigSeriesGroup[],
    timezone: string,
    showThresholdPlotlines: boolean;
    realTimestamps: boolean;
    hideSingleGaps: boolean;
    convertAllValues: boolean;
    labels: boolean
}

export interface CustomChartConfigSeries {
    series: { [index: string]: any }
}


// We don't use enums here, because our keys are strings and our values are not unique
export class CHART_TYPE {
    static DEFAULT = '0';
    static LINE = '1';
    static COLUMN = '2';
    static STACKEDCOLUMN = '3';
    static HEATMAP = '4';
    static STACKEDAREA = '5';
    static PIE = '6';
    static WIDGET_MULTI_SERIES_SINGLE_POINT = '7';
    static WIDGET_MULTI_SERIES = '8';
    static GAUGE = '9';
    static BOX_PLOT = '10';
}

export class CHART_REAL_TIMESTAMPS {
    static DEFAULT = '0';
    static ENABLED = '1';  // Real timestamps
    static DISABLED = '2'; // Rounded timestamps
}

export class CHART_AGGREGATION_TYPE {
    static NONE = '0';
    static DEFAULT = '0';
    static AVG = '1';
    static SUM = '2';
    static MIN = '3';
    static MAX = '4';
    static MAXDIFF = '5'; // aka "Period Delta"
    static MODE = '6';
    static MEDIAN = '7';
    static STDDEV = '8';
    static MAXDIFFPOSITIVE = '9';
    static BOXPLOT = '10';
    static PERCENTILE95 = '11';
    static PERCENTILE5 = '12';
}

export class CHART_AGGREGATION_PERIOD {
    static NONE = '0';
    static DEFAULT = '0';
    static HOUR = '1';
    static DAY = '2';
    static WEEK = '3';
    static MONTH_1 = '4';
    static MONTH_3 = '5';
    static MONTH_6 = '6';
    static YEAR = '7';
    static MINUTE_15 = '8';
    static MINUTE_30 = '9';
    static RANGE = '10';
}

export class CHART_LABELS {
    static DEFAULT = '0';
    static ENABLED = '1';
    static DISABLED = '2';
}

export class CHART_DATA_RANGE {
    static DEFAULT = '0';
    static DAY_1 = '1';
    static DAY_TODAY = '7';
    static DAY_YESTERDAY = '8';
    static WEEK_1 = '2';
    static WEEK_CURRENT = '9';
    static WEEK_LAST = '10';
    static MONTH_1 = '3';
    static MONTH_3 = '4';
    static MONTH_6 = '5';
    static MONTH_CURRENT = '11';
    static MONTH_LAST = '12';
    static YEAR_1 = '6';
    static YEAR_CURRENT = '13';
    static YEAR_LAST = '14';
    static CUSTOM = '15';
    static CUSTOM_DAILY = '16';
    static CUSTOM_MONTHLY_BILLING = '17';
}

export class CHART_DATA_ZOOM {
    static DEFAULT = '0';
    static HOUR_1 = '1';
    static DAY_1 = '2';
    static WEEK_1 = '3';
    static MONTH_1 = '4';
    static MONTH_3 = '6';
    static MONTH_6 = '7';
    static YEAR_1 = '5';
}

export class CHART_CONNECT_TO_TILES {
    static CONNECT_TO_ALL = '0';
    static ENABLED = '1';
    static DISABLED = '2';
}

export class CHART_SIZE {
    static DEFAULT = '6';
    static SIZE_1 = '1';
    static SIZE_2 = '2';
    static SIZE_3 = '3';
    static SIZE_4 = '4';
    static SIZE_5 = '5';
    static SIZE_6 = '6';
}

export class CHART_IS_TALL {
    static DISABLED = '0';
    static ENABLED = '1';
}

export class CHART_HIDE_SINGLE_GAPS {
    static DEFAULT = '0';
    static ENABLED = '1';
    static DISABLED = '2';

}

export class CHART_CONVERT_ALL_DATA {
    static DEFAULT = '0';
    static ENABLED = '1';
    static DISABLED = '2';
}

export class CHART_HIDE_EXPORTS {
    static DEFAULT = '0';
    static ENABLED = '1';
    // Disabled, in this case, means to *not* be hidden
    static DISABLED = '2';
}

export class CHART_THRESHOLDS {
    static DEFAULT = '0';
    static ENABLED = '1';
    static DISABLED = '2';
}

export class DAILY_TIME_MODE {
    static DEFAULT = '0';
    static FULL_RANGE = '1';
    static UP_TO_NOW = '2';
}

export interface minMaxTimeStampOptions {
    initial?: boolean,
    channel: Backbone.Model,
    widget: DashboardWidget,
    customDataRangeFunc: any,
    isDefaultChart?: boolean,
    minDataRequested?: number,
    maxDataRequested?: number,
    dashboardChannelCollection?: DashboardChannelCollection,
    graphedDashboardChannelIds?: number[],
    user: User,
}
export class ChartHelper {

    static getDataRangeEquivalent(aggregationPeriod: CHART_AGGREGATION_PERIOD) {
        switch(aggregationPeriod) {
            case CHART_AGGREGATION_PERIOD.DAY:
            case CHART_AGGREGATION_PERIOD.HOUR:
            case CHART_AGGREGATION_PERIOD.MINUTE_15:
            case CHART_AGGREGATION_PERIOD.MINUTE_30:
                return CHART_DATA_RANGE.DAY_1;
            case CHART_AGGREGATION_PERIOD.WEEK:
                return CHART_DATA_RANGE.WEEK_1;
            case CHART_AGGREGATION_PERIOD.MONTH_1:
                return CHART_DATA_RANGE.MONTH_1;
            case CHART_AGGREGATION_PERIOD.MONTH_3:
                return CHART_DATA_RANGE.MONTH_3;
            case CHART_AGGREGATION_PERIOD.MONTH_6:
                return CHART_DATA_RANGE.MONTH_6;
            case CHART_AGGREGATION_PERIOD.YEAR:
                return CHART_DATA_RANGE.YEAR_1;
            case CHART_AGGREGATION_PERIOD.RANGE:
                return CHART_DATA_RANGE.WEEK_1;
            default:
                return CHART_DATA_RANGE.WEEK_1;
        }
    }

    // Make sure the zoom period does not exceed the amount of data loaded.
    // i.e. if zoom = 1 week, and data range = 1 day the effective zoom becomes 1 day
    static effectiveZoomHelper(allowedZoomLevels: string[], defaultZoom: string, zoom: string) {
        return _.includes(allowedZoomLevels, zoom) ? zoom : defaultZoom;
    }

    static effectiveZoom(zoom: string, range: string) {
        if (zoom === CHART_DATA_ZOOM.DEFAULT) {
            return CHART_DATA_ZOOM.HOUR_1;
        }

        switch (range) {
            case CHART_DATA_RANGE.DEFAULT:
                return ChartHelper.effectiveZoomHelper([
                    CHART_DATA_ZOOM.DEFAULT,
                    CHART_DATA_ZOOM.HOUR_1,
                ], CHART_DATA_ZOOM.DAY_1, zoom);
            case CHART_DATA_RANGE.DAY_1:
            case CHART_DATA_RANGE.DAY_TODAY:
            case CHART_DATA_RANGE.DAY_YESTERDAY:
                return ChartHelper.effectiveZoomHelper([
                    CHART_DATA_ZOOM.HOUR_1,
                ], CHART_DATA_ZOOM.DAY_1, zoom);
            case CHART_DATA_RANGE.WEEK_1:
            case CHART_DATA_RANGE.WEEK_CURRENT:
            case CHART_DATA_RANGE.WEEK_LAST:
                return ChartHelper.effectiveZoomHelper([
                    CHART_DATA_ZOOM.HOUR_1,
                    CHART_DATA_ZOOM.DAY_1,
                ], CHART_DATA_ZOOM.WEEK_1, zoom);
            case CHART_DATA_RANGE.MONTH_1:
            case CHART_DATA_RANGE.MONTH_CURRENT:
            case CHART_DATA_RANGE.MONTH_LAST:
                return ChartHelper.effectiveZoomHelper([
                    CHART_DATA_ZOOM.HOUR_1,
                    CHART_DATA_ZOOM.DAY_1,
                    CHART_DATA_ZOOM.WEEK_1
                ], CHART_DATA_ZOOM.MONTH_1, zoom);
            case CHART_DATA_RANGE.MONTH_3:
                return ChartHelper.effectiveZoomHelper([
                    CHART_DATA_ZOOM.HOUR_1,
                    CHART_DATA_ZOOM.DAY_1,
                    CHART_DATA_ZOOM.WEEK_1,
                    CHART_DATA_ZOOM.MONTH_1
                ], CHART_DATA_ZOOM.MONTH_3, zoom);
            case CHART_DATA_RANGE.MONTH_6:
                return ChartHelper.effectiveZoomHelper([
                    CHART_DATA_ZOOM.HOUR_1,
                    CHART_DATA_ZOOM.DAY_1,
                    CHART_DATA_ZOOM.WEEK_1,
                    CHART_DATA_ZOOM.MONTH_1,
                    CHART_DATA_ZOOM.MONTH_3,
                ], CHART_DATA_ZOOM.MONTH_6, zoom);
            case CHART_DATA_RANGE.YEAR_1:
            case CHART_DATA_RANGE.YEAR_CURRENT:
            case CHART_DATA_RANGE.YEAR_LAST:
                return ChartHelper.effectiveZoomHelper([
                    CHART_DATA_ZOOM.HOUR_1,
                    CHART_DATA_ZOOM.DAY_1,
                    CHART_DATA_ZOOM.WEEK_1,
                    CHART_DATA_ZOOM.MONTH_1,
                    CHART_DATA_ZOOM.MONTH_3,
                    CHART_DATA_ZOOM.MONTH_6,
                ], CHART_DATA_ZOOM.YEAR_1, zoom);
        }
        return zoom;
    }

    static chartZoomAndDataRangeEqual(zoom, range) {
        switch (ChartHelper.effectiveZoom(zoom, range)) {
            case CHART_DATA_ZOOM.DAY_1:
                return _.includes([
                    CHART_DATA_RANGE.DAY_1,
                    CHART_DATA_RANGE.DAY_YESTERDAY,
                    CHART_DATA_RANGE.DAY_TODAY
                ], range);
            case CHART_DATA_ZOOM.WEEK_1:
                return _.includes([
                    CHART_DATA_RANGE.WEEK_1,
                    CHART_DATA_RANGE.WEEK_CURRENT,
                    CHART_DATA_RANGE.WEEK_LAST
                ], range);
            case CHART_DATA_ZOOM.MONTH_1:
                return _.includes([
                    CHART_DATA_RANGE.MONTH_1,
                    CHART_DATA_RANGE.MONTH_CURRENT,
                    CHART_DATA_RANGE.MONTH_LAST
                ], range);
            case CHART_DATA_ZOOM.MONTH_3:
                return range === CHART_DATA_RANGE.MONTH_3;
            case CHART_DATA_ZOOM.MONTH_6:
                return range === CHART_DATA_RANGE.MONTH_6;
            case CHART_DATA_ZOOM.YEAR_1:
                return _.includes([
                    CHART_DATA_RANGE.YEAR_1,
                    CHART_DATA_RANGE.YEAR_CURRENT,
                    CHART_DATA_RANGE.YEAR_LAST
                ], range);
        }
        return false;
    }

    // Helper functions to compare chart extremes
    static withinPercent(value, expected, percent) {
        return value > expected * (1 - percent) && value < expected * (1 + percent);
    }

    // Helper function to compare chart extremes
    static withinConstant(value, expected, constant) {
        return value > (expected - constant) && value < (expected + constant);
    }

    // Helper function to convert constants
    static  getHighchartsTypeFromChartType(type: string) {
        switch (type) {
            case CHART_TYPE.DEFAULT:
            case CHART_TYPE.LINE:
                return 'line';
            case CHART_TYPE.COLUMN:
            case CHART_TYPE.STACKEDCOLUMN:
                return 'column';
            case CHART_TYPE.STACKEDAREA:
                return 'area';
            case CHART_TYPE.HEATMAP:
                return 'heatmap';
            case CHART_TYPE.PIE:
                return 'pie';
            case CHART_TYPE.GAUGE:
                return 'gauge';
            case CHART_TYPE.BOX_PLOT:
                return 'boxplot';
        }
    }

    static getHighChartsApproximationFromAggregationType(aggType: string) {
        switch (aggType) {
            case CHART_AGGREGATION_TYPE.AVG:
                return 'average';
            case CHART_AGGREGATION_TYPE.SUM:
                return 'sum';
            case CHART_AGGREGATION_TYPE.MIN:
                return 'low';
            case CHART_AGGREGATION_TYPE.MAX:
                return 'high';
            default:
                return 'average';
        }
    }

    static isDataRangeDuration(range: string) {
        return _.includes([
            CHART_DATA_RANGE.DEFAULT,
            CHART_DATA_RANGE.DAY_1,
            CHART_DATA_RANGE.WEEK_1,
            CHART_DATA_RANGE.MONTH_1,
            CHART_DATA_RANGE.MONTH_3,
            CHART_DATA_RANGE.MONTH_6,
            CHART_DATA_RANGE.YEAR_1,
        ], range);
    }

    static getNumDaysFromDataRange(range: string) {
        switch (range) {
            case CHART_DATA_RANGE.DEFAULT:
                return 3;
            case CHART_DATA_RANGE.DAY_1:
                return 1;
            case CHART_DATA_RANGE.WEEK_1:
                return 7;
            case CHART_DATA_RANGE.MONTH_1:
                return 31;
            case CHART_DATA_RANGE.MONTH_3:
                return 90;
            case CHART_DATA_RANGE.MONTH_6:
                return 180;
            case CHART_DATA_RANGE.YEAR_1:
                return 365;
        }
    }

    static isEnabled(param: string) {
        return param === '0' || param === '1';
    }

    static getStartMomentTimezoneFromDataRange(dataRange, timezone) {
        var startTimestamp;
        switch (<any>dataRange.range) {
            case CHART_DATA_RANGE.DAY_TODAY:
                startTimestamp = moment().tz(timezone).startOf('day').unix();
                break;
            case CHART_DATA_RANGE.DAY_YESTERDAY:
                startTimestamp = moment().tz(timezone).subtract(1, 'day').startOf('day').unix();
                break;
            case CHART_DATA_RANGE.WEEK_CURRENT:
                startTimestamp = moment().tz(timezone).startOf('isoWeek').unix();
                break;
            case CHART_DATA_RANGE.WEEK_LAST:
                // bug in moment.js typescript def: https://github.com/moment/moment/issues/5007
                startTimestamp = moment().tz(timezone).subtract(<any>1, <any>'isoWeek').startOf('isoWeek').unix();
                break;
            case CHART_DATA_RANGE.MONTH_CURRENT:
                startTimestamp = moment().tz(timezone).startOf('month').unix();
                break;
            case CHART_DATA_RANGE.MONTH_LAST:
                startTimestamp = moment().tz(timezone).subtract(1, 'month').startOf('month').unix();
                break;
            case CHART_DATA_RANGE.YEAR_CURRENT:
                startTimestamp = moment().tz(timezone).startOf('year').unix();
                break;
            case CHART_DATA_RANGE.YEAR_LAST:
                startTimestamp = moment().tz(timezone).subtract(1, 'year').startOf('year').unix();
                break;
            case CHART_DATA_RANGE.CUSTOM:
            case CHART_DATA_RANGE.CUSTOM_MONTHLY_BILLING:
                startTimestamp = dataRange.from;
                break;
            case CHART_DATA_RANGE.CUSTOM_DAILY:
                var now = moment().tz(timezone);
                var todayDailyFromUnixValue = now.startOf('day').add(dataRange.dailyFrom, 'seconds').unix();
                var yesterdayDailyFromUnixValue = now.tz(timezone).startOf('day').add(dataRange.dailyFrom, 'seconds').unix();
                if (Number(dataRange.dailyTo) < Number(dataRange.dailyFrom)) {
                    startTimestamp = todayDailyFromUnixValue;
                } else if (dataRange.dailyTo === dataRange.dailyFrom) {
                    startTimestamp = yesterdayDailyFromUnixValue - 86400;
                } else {
                    startTimestamp = yesterdayDailyFromUnixValue;
                }
                break;
        }
        return startTimestamp;
    }

    static getEndMomentTimezoneFromDataRange(dataRange, timezone) {
        if (dataRange.range === CHART_DATA_RANGE.DAY_TODAY) {
            return moment().tz(timezone).endOf('day').unix();
        } else if (dataRange.range === CHART_DATA_RANGE.DAY_YESTERDAY) {
            return moment().tz(timezone).subtract(1, 'day').endOf('day').unix();
        } else if (dataRange.range === CHART_DATA_RANGE.WEEK_CURRENT) {
            return moment().tz(timezone).endOf('week').unix();
        } else if (dataRange.range === CHART_DATA_RANGE.WEEK_LAST) {
            // bug in moment.js typescript def: https://github.com/moment/moment/issues/5007
            return moment().tz(timezone).subtract(<any>1, <any>'isoWeek').endOf('isoWeek').unix();
        } else if (dataRange.range === CHART_DATA_RANGE.MONTH_CURRENT) {
            return moment().tz(timezone).endOf('month').unix();
        } else if (dataRange.range === CHART_DATA_RANGE.MONTH_LAST) {
            return moment().tz(timezone).subtract(1, 'month').endOf('month').unix();
        } else if (dataRange.range === CHART_DATA_RANGE.YEAR_CURRENT) {
            return moment().tz(timezone).endOf('year').unix();
        } else if (dataRange.range === CHART_DATA_RANGE.YEAR_LAST) {
            return moment().tz(timezone).subtract(1, 'year').endOf('year').unix();
        } else if (dataRange.range === CHART_DATA_RANGE.CUSTOM || dataRange.range === CHART_DATA_RANGE.CUSTOM_MONTHLY_BILLING) {
            return dataRange.to;
        } else if (dataRange.range === CHART_DATA_RANGE.CUSTOM_DAILY) {
            var todayDailyToUnixValue = moment().tz(timezone).startOf('day').add(dataRange.dailyTo, 'seconds').unix();
            return todayDailyToUnixValue;
        }
    }

    // Buttons used on the chart to load data and zoom
    // Each button in an array of label, zoom number, and expected extreme difference
    static CHARTZOOMBUTTONS = [
        {label: '1h', zoomLevel: CHART_DATA_ZOOM.HOUR_1, extremesDifference: 60 * 60 * 1000},
        {label: '1d', zoomLevel: CHART_DATA_ZOOM.DAY_1, extremesDifference: 60 * 60 * 24 * 1000},
        {label: '1w', zoomLevel: CHART_DATA_ZOOM.WEEK_1, extremesDifference: 60 * 60 * 24 * 7 * 1000},
        {label: '1m', zoomLevel: CHART_DATA_ZOOM.MONTH_1, extremesDifference: 60 * 60 * 24 * 31 * 1000},
        {label: '3m', zoomLevel: CHART_DATA_ZOOM.MONTH_3, extremesDifference: 60 * 60 * 24 * 92 * 1000},
        {label: '6m', zoomLevel: CHART_DATA_ZOOM.MONTH_6, extremesDifference: 60 * 60 * 24 * 183 * 1000},
        {label: '1y', zoomLevel: CHART_DATA_ZOOM.YEAR_1, extremesDifference: 60 * 60 * 24 * 365 * 1000},
        {label: 'All', zoomLevel: 'Infinity', extremesDifference: Infinity}
    ];

    static getMinTimestampToRequest(options: minMaxTimeStampOptions) {
        var minTimeStamp;
        var dataRange = ChartHelper.getChartDataRange(options.widget, options.user, options.customDataRangeFunc, options.isDefaultChart);
        var timezone = this.getChartTimezone(options.widget, options.user);

        if (ChartHelper.isDataRangeDuration(dataRange.range)) {
            const dashboardChannels: any = options.dashboardChannelCollection.where((dashboardChannel) => {
                return _.includes(options.graphedDashboardChannelIds, dashboardChannel.get('dashboard_channel_id'))
            });
            // Get the greatest timestamp value from the channels
            const maxTimeStamp = _.reduce(dashboardChannels, (maxTimestamp, dashboardChannel) => {
                const timestamp = dashboardChannel.get('channel_last_timestamp');
                // Check if timestamp is defined and greater than the current maxTimestamp
                if (timestamp !== undefined && (maxTimestamp === undefined || timestamp > maxTimestamp)) {
                    return timestamp;
                }
                // If not, keep the current maxTimestamp
                return maxTimestamp;
            }, undefined);

            var numDays = ChartHelper.getNumDaysFromDataRange(dataRange.range);

            if (maxTimeStamp) {
                minTimeStamp = maxTimeStamp - (numDays * 86400);
            } else {
                minTimeStamp = moment().utc().subtract(numDays, 'days').unix()
            }
        } else {
            minTimeStamp = ChartHelper.getStartMomentTimezoneFromDataRange(dataRange, timezone);
        }

        var dRange = options.widget.getParamValue('CHART_DATA_RANGE', CHART_DATA_RANGE.DEFAULT);
        var dailyTimeMode = options.widget.getParamValue('DAILY_TIME_MODE', DAILY_TIME_MODE.DEFAULT);
        var maxTimeStamp = options.maxDataRequested;
        if (dRange === CHART_DATA_RANGE.CUSTOM_DAILY) {
            if (dailyTimeMode === DAILY_TIME_MODE.FULL_RANGE || dailyTimeMode === DAILY_TIME_MODE.DEFAULT) {
                if (maxTimeStamp && maxTimeStamp < minTimeStamp) {
                    // If we haven't completed today's range yet, switch to tomorrow
                    var timezone = ChartHelper.getChartTimezone(options.widget, options.user);
                    minTimeStamp =  moment(Number(minTimeStamp) * 1000).tz(timezone).subtract(1, 'day').unix();
                }
            } else if (dailyTimeMode === DAILY_TIME_MODE.UP_TO_NOW) {
                if (maxTimeStamp < minTimeStamp && Number(dataRange.dailyTo) > Number(dataRange.dailyFrom)) {
                    var timezone = ChartHelper.getChartTimezone(options.widget, options.user);
                    minTimeStamp =  moment(Number(minTimeStamp) * 1000).tz(timezone).subtract(1, 'day').unix();
                }
            }

            // If min and max timestamps are the same, subtract a day from min to give a full day's range of data
            // This handles for the case where a user enters a custom daily range where both From and To are the same time
            if (minTimeStamp === maxTimeStamp) minTimeStamp -= 86400
        }
        return minTimeStamp;
    }

    static getMaxTimestampToRequest(options: minMaxTimeStampOptions) {
        let maxTimeStamp;
        var dataRange = ChartHelper.getChartDataRange(options.widget, options.user, options.customDataRangeFunc, options.isDefaultChart);
        var timezone = this.getChartTimezone(options.widget, options.user);

        if (ChartHelper.isDataRangeDuration(dataRange.range)) {
            const dashboardChannels: any = options.dashboardChannelCollection.where((dashboardChannel) => {
                return _.includes(options.graphedDashboardChannelIds, dashboardChannel.get('dashboard_channel_id'))
            });
            // Get the greatest timestamp value from the channels
            maxTimeStamp = _.reduce(dashboardChannels, (maxTimestamp, dashboardChannel) => {
                const timestamp = dashboardChannel.get('channel_last_timestamp');
                // Check if timestamp is defined and greater than the current maxTimestamp
                if (timestamp !== undefined && (maxTimestamp === undefined || timestamp > maxTimestamp)) {
                    return timestamp;
                }
                // If not, keep the current maxTimestamp
                return maxTimestamp;
            }, undefined);
            // Fallback to now when no channel has last_timestamp (e.g. report aggregate widget with empty graphed ids)
            if (maxTimeStamp === undefined) {
                maxTimeStamp = moment().utc().unix();
            }

        } else {
            maxTimeStamp = ChartHelper.getEndMomentTimezoneFromDataRange(dataRange, timezone);
        }

        var dRange = options.widget.getParamValue('CHART_DATA_RANGE', CHART_DATA_RANGE.DEFAULT);
        var dailyTimeMode = options.widget.getParamValue('DAILY_TIME_MODE', DAILY_TIME_MODE.DEFAULT);
        if (dRange === CHART_DATA_RANGE.CUSTOM_DAILY) {
            if (dailyTimeMode === DAILY_TIME_MODE.FULL_RANGE || dailyTimeMode === DAILY_TIME_MODE.DEFAULT) {
                if (maxTimeStamp && maxTimeStamp > moment().unix()) {
                    // If we haven't completed today's range yet, switch to tomorrow
                    var timezone = ChartHelper.getChartTimezone(options.widget, options.user);
                    return moment(Number(maxTimeStamp) * 1000).tz(timezone).subtract(1, 'day').unix();
                } else {
                    return maxTimeStamp;
                }
            } else if (dailyTimeMode === DAILY_TIME_MODE.UP_TO_NOW) {
                return maxTimeStamp;
            }
        } else {
            return maxTimeStamp;
        }
    }

    static getChartDataRange(widget: DashboardWidget, user: User, customDataRangeFunc: any, isDefaultChart?: boolean) {
        var timezone = this.getChartTimezone(widget, user);

        if (isDefaultChart && customDataRangeFunc) {
            var input = customDataRangeFunc();
            if (this.isValidCustomDataRangeInput(input)) {
                return {
                    range: CHART_DATA_RANGE.CUSTOM,
                    from: input.from.unix(),
                    to: input.to.unix()
                }
            } else {
                return {
                    range: CHART_DATA_RANGE.DEFAULT,
                    from: null,
                    to: null
                }
            }
        } else {
            return {
                range: widget.getParamValue('CHART_DATA_RANGE', CHART_DATA_RANGE.DEFAULT),
                from: widget.getParamValue(
                    'CHART_DATA_RANGE_FROM',
                    moment().tz(timezone).startOf('day').unix()
                ),
                to: widget.getParamValue(
                    'CHART_DATA_RANGE_TO',
                    moment().tz(timezone).endOf('day').unix()
                ),
                dailyFrom: widget.getParamValue(
                    'DAILY_TIME_FROM',
                    0 // Start of day
                ),
                dailyTo: widget.getParamValue(
                    'DAILY_TIME_TO',
                    60 * 60 * 24 // End of day
                ),
            }
        }
    }

    static getChartTimezone(widget: DashboardWidget, user: User) {
        return widget.getParamValue('CHART_TIMEZONE', user.get('timezone'));
    }

    static isValidCustomDataRangeInput(input) {
        var customRange = input.fromText.length && input.toText.length;
        return customRange && input.from.isValid() && input.to.isValid() && input.to.isAfter(input.from);
    }


}



// WEBPACK FOOTER //
// ./srv/public/js/views/dashboard/widgets/chart/ChartHelper.ts